import { useState, useCallback, useRef, ReactNode } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider,
  Switch, TextField, Chip, CircularProgress, Paper,
  List, ListItem, Avatar, FormControlLabel,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { FancyButton } from '../comps/FancyButtons'
import { FS_MD, FS_XL, FS_XS, ICON_MD } from '../common/theme'
import { useApp, useAppActions } from '../AppContext'
import { addToast } from '../services/Toasts'
import taapi from '../services/taapi'
import { logout } from '../common/auth'
import FancyProgressArc from '../comps/FancyProgressArc'
import type { PersonalSettings } from '../types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatQuotaDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function SectionHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
      {children}
    </Box>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SettingsTab() {
  const { acInit } = useApp()
  const { setAcInit } = useAppActions()

  const quota = acInit?.quota ?? {}
  const personalOpts = acInit?.personaloptions ?? {}
  const orgOptions = acInit?.orgoptions ?? {}
  const orgName = acInit?.orgname ?? ''
  const usernames = (acInit?.usernames ?? []) as string[]
  const modClaude = acInit?.mod_claude ?? false

  const [local, setLocal] = useState<PersonalSettings>({
    useclaudeforanswers: personalOpts.useclaudeforanswers ?? false,
    oversizesources: personalOpts.oversizesources ?? 10000,
    reprocessshortanswers: personalOpts.reprocessshortanswers ?? false,
    refinefoundsources: personalOpts.refinefoundsources ?? false,
    cacherequests: personalOpts.cacherequests ?? false,
    showhelpvideos: personalOpts.showhelpvideos ?? false,
  })

  const [refreshing, setRefreshing] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const persistSettings = useCallback(async (next: PersonalSettings) => {
    setSaveState('saving')
    try {
      await taapi.post('/api2/acsavepersonal', next)
      setAcInit({ ...acInit, personaloptions: { ...personalOpts, ...next } })
      setSaveState('saved')
      addToast('success', 'Settings saved')
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => setSaveState('idle'), 2000)
    } catch {
      setSaveState('idle')
      addToast('error', 'Failed to save settings')
    }
  }, [acInit, personalOpts, setAcInit])

  const toggle = useCallback((key: keyof PersonalSettings, value: boolean | number) => {
    setLocal(prev => {
      const next = { ...prev, [key]: value }
      persistSettings(next)
      return next
    })
  }, [persistSettings])

  const handleRefreshQuota = useCallback(async () => {
    setRefreshing(true)
    try {
      const result = await taapi.get('/api2/acinit', {})
      if (result) setAcInit(result)
    } finally {
      setRefreshing(false)
    }
  }, [setAcInit])

  const perint = quota.perint ?? 0

  return (
    <Box data-testid="settings_root" sx={{ flex: 1, overflowY: 'auto', py: 1.5 }}>
      <Box sx={{ maxWidth: 640, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2, px: 1.5 }}>

        {/* ── Quota ──────────────────────────────────────────────────── */}
        <Paper variant="outlined">
          <SectionHeader title="Quota">
            <Tooltip title="Refresh Quota">
              <span>
                <IconButton data-testid="settings_quota_refresh_btn" size="small" onClick={handleRefreshQuota} disabled={refreshing}>
                  {refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: ICON_MD }} />}
                </IconButton>
              </span>
            </Tooltip>
          </SectionHeader>

          <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* do not use zerona here */}
              <FancyProgressArc value={perint} reverse tooltip="Quota Used" scale={1.3} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  {quota.overquota && <ErrorOutlineIcon sx={{ fontSize: ICON_MD, color: 'error.main', mr: 0.25 }} />}
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {(quota.totaltokens ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">tokens used</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  of&nbsp;
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {(quota.quota ?? 0).toLocaleString()}
                  </Box>
                  &nbsp;token quota
                </Typography>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Period started</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatQuotaDate(quota.currentperiodstart)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Resets</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatQuotaDate(quota.currentperiodend)}</Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Contact&nbsp;
              <Box component="a" href="mailto:sales@contractfinderpro.com" sx={{ color: 'primary.main' }}>
                sales@contractfinderpro.com
              </Box>
              &nbsp;to increase your quota.
            </Typography>
          </Box>
        </Paper>

        {/* ── Personal Settings ──────────────────────────────────────── */}
        <Paper variant="outlined">
          <SectionHeader title="Personal Settings">
            {saveState === 'saving' && <CircularProgress size={14} sx={{ ml: 1 }} />}
            {saveState === 'saved' && <CheckCircleOutlineIcon sx={{ fontSize: ICON_MD, color: 'success.main', ml: 1 }} />}
          </SectionHeader>

          {modClaude && (
            <FormControlLabel
              data-testid="settings_useclaude_row"
              control={
                <Switch
                  inputProps={{ 'data-testid': 'settings_useclaude_switch' } as any}
                  checked={local.useclaudeforanswers}
                  onChange={(e) => toggle('useclaudeforanswers', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontSize: FS_MD }}>Use Claude for Answers</Typography>
                  <Typography variant="caption" color="text.secondary">Generate answers using the Claude AI model</Typography>
                </Box>
              }
              labelPlacement="start"
              sx={{ mx: 0, px: 2, py: 0.75, width: '100%', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}
            />
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, gap: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontSize: FS_MD }}>Oversize Warning Threshold</Typography>
              <Typography variant="caption" color="text.secondary">Warn when a source document exceeds this token count</Typography>
            </Box>
            <TextField
              type="number"
              size="small"
              value={local.oversizesources}
              inputProps={{ min: 0, max: 100000, step: 1000, 'data-testid': 'settings_oversize_input' }}
              onChange={(e) => setLocal(v => ({ ...v, oversizesources: Number(e.target.value) }))}
              onBlur={() => persistSettings(local)}
              sx={{ width: 110, '& input': { fontSize: '0.8rem', py: 0.5 } }}
            />
          </Box>

          {([
            ['reprocessshortanswers', 'Advanced Word Count Precision', 'More accurate word counts with longer processing time'],
            ['refinefoundsources', 'Advanced Find Precision', 'More accurate source finding with longer processing time'],
            ['cacherequests', 'Cache Requests', 'Cache repeated API calls to minimise redundant processing'],
            ['showhelpvideos', 'Show Quick Tour Videos', 'Display onboarding tour videos throughout the app'],
          ] as [keyof PersonalSettings, string, string][]).map(([key, label, desc]) => (
            <FormControlLabel
              key={key}
              data-testid={`settings_row_${key}`}
              control={
                <Switch
                  inputProps={{ 'data-testid': `settings_switch_${key}` } as any}
                  checked={local[key] as boolean}
                  onChange={(e) => toggle(key, e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontSize: FS_MD }}>{label}</Typography>
                  <Typography variant="caption" color="text.secondary">{desc}</Typography>
                </Box>
              }
              labelPlacement="start"
              sx={{ mx: 0, px: 2, py: 0.75, width: '100%', justifyContent: 'space-between', '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' } }}
            />
          ))}
        </Paper>

        {/* ── Organisation Settings ──────────────────────────────────── */}
        <Paper variant="outlined">
          <SectionHeader title="Organisation Settings" />
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, gap: 2 }}>
            <Typography variant="body2" sx={{ flex: 1, fontSize: FS_MD }}>AI Model</Typography>
            <Chip size="small" label={orgOptions.aimodel ?? 'Default'} sx={{ fontSize: FS_XS }} />
          </Box>
        </Paper>

        {/* ── Account ─────────────────────────────────────────────────── */}
        <Paper variant="outlined">
          <SectionHeader title="Account" />
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontSize: FS_MD }}>Sign out</Typography>
              <Typography variant="caption" color="text.secondary">
                End your session and return to the login screen
              </Typography>
            </Box>
            <FancyButton
              testId="settings_logout_btn"
              color="#DC143C"
              onClick={logout}
            >
              Logout
            </FancyButton>
          </Box>
        </Paper>

        {/* ── Organisation + Users ───────────────────────────────────── */}
        <Paper variant="outlined">
          <SectionHeader title={orgName || 'Organisation'} />
          {usernames.length === 0 ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">No users</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {usernames.map((user, i) => (
                <ListItem
                  key={i}
                  data-testid="settings_user_row"
                  data-user={user}
                  sx={{ px: 2, py: 0.5, gap: 1, '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' } }}
                >
                  <Avatar sx={{ width: 24, height: 24, fontSize: FS_XS, bgcolor: 'primary.main' }}>
                    <PersonOutlineIcon sx={{ fontSize: FS_XL }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ fontSize: FS_MD }}>{user}</Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

      </Box>
    </Box>
  )
}
